/**
 * "Recent" panel that sits below the search field on the v2 Landing
 * page. Shows the user's last-clicked pre-launch concepts so they
 * can jump back into a workflow without retyping.
 *
 * Design intent: minimalist, modern. A subtle section heading, a
 * grid of soft card-style chips, and tiny meta-controls (View more,
 * Clear) at the bottom edge. We deliberately keep the visual weight
 * lower than the search field — recent items are a convenience, not
 * the primary affordance.
 *
 * Hidden entirely when the recent list is empty so first-time users
 * don't see a confusing empty section.
 */

import { observer } from 'mobx-react';
import { Clock, X } from 'lucide-react';

import { useStore } from 'hooks/useStore';
import { RecentConceptEntry } from 'utils/recentConceptStorage';
import styles from './recentConcepts.module.scss';

// Show 6 by default — the grid is 2 columns wide, so 6 fills it
// cleanly without leaving an empty cell. We only surface the
// "View more" button when there are MORE than 6 entries, otherwise
// it would point at nothing.
const COLLAPSED_VISIBLE_COUNT = 6;

const RecentConcepts = observer(() => {
  const {
    ProvisionalForecastV2Store: {
      recentConcepts,
      showAllRecent,
      toggleShowAllRecent,
      clearRecent,
      selectConceptAndOpenSidebar,
    },
    UserStore: { userMetadata },
  } = useStore();
  const userEmail = userMetadata?.userInfo?.email ?? null;

  if (recentConcepts.length === 0) return null;

  const visible = showAllRecent
    ? recentConcepts
    : recentConcepts.slice(0, COLLAPSED_VISIBLE_COUNT);
  const hasMore = recentConcepts.length > COLLAPSED_VISIBLE_COUNT;

  const handleCardClick = (entry: RecentConceptEntry) => {
    selectConceptAndOpenSidebar(
      { GFC: entry.GFC, Concept: entry.Concept },
      userEmail
    );
  };

  return (
    <section className={styles.wrap} aria-label="Recently viewed concepts">
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Clock size={14} className={styles.headerIcon} aria-hidden />
          <span className={styles.headerLabel}>Recent</span>
          <span className={styles.headerCount}>{recentConcepts.length}</span>
        </div>
        <button
          type="button"
          className={styles.clearBtn}
          onClick={() => clearRecent(userEmail)}
          aria-label="Clear recent concepts"
        >
          Clear
        </button>
      </header>

      <ul
        className={`${styles.grid} ${showAllRecent ? styles.gridScroll : ''}`}
      >
        {visible.map(entry => (
          <li key={entry.GFC} className={styles.item}>
            <button
              type="button"
              className={styles.card}
              onClick={() => handleCardClick(entry)}
              title={entry.Concept}
            >
              <span className={styles.cardConcept}>{entry.Concept}</span>
              <span className={styles.cardGfc}>GFC {entry.GFC}</span>
            </button>
          </li>
        ))}
      </ul>

      {hasMore && (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.toggleBtn}
            onClick={toggleShowAllRecent}
          >
            {showAllRecent ? (
              <>
                <X size={12} aria-hidden /> Show less
              </>
            ) : (
              <>
                View {recentConcepts.length - COLLAPSED_VISIBLE_COUNT} more
              </>
            )}
          </button>
        </div>
      )}
    </section>
  );
});

export default RecentConcepts;
